import { Dimmer, Grid, Icon, Loader, Popup } from 'semantic-ui-react';
import {
  ProcedureDto,
  ProcedureGroupDto,
  ProcedureGroupSearchRequest,
  TariffDto,
  UpsertProcedureGroupDto
} from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { deleteProcedureGroup, findProcedureGroupById, searchProcedureGroup } from 'services/procedureGroupServices';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import { getAllTariffs } from 'services/tariffServices';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import UpsertProcedureGroupsForm from 'components/procedures/UpsertProcedureGroupsForm';
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledActionButton from 'components/layout/StyledActionButton';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import DeleteActionButton from 'components/DeleteActionButton';
import SearchResultComponent from 'components/SearchResultComponent';

const initialProcedureGroupsSearch: Partial<ProcedureGroupSearchRequest> = {};

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

interface ProcedureGroupsFieldProps extends FormState {
  values: Partial<ProcedureGroupSearchRequest>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundProcedureGroups: Array<ProcedureGroupDto>,
  procedureGroupsLoaded: boolean,
  procedureGroup?: UpsertProcedureGroupDto,
  procedureGroupId?: number,
  addEditModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  tariffs: Array<TariffDto>,
  selectedTariffId?: number,
  tarmedTariff?: Boolean,
  procedures?: Array<ProcedureDto>,
  searchValues: Partial<ProcedureGroupSearchRequest>
  availableProcedures?: Array<number>,
  errorMessage?: string
}

class ProcedureGroupsView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundProcedureGroups: [],
      procedureGroupsLoaded: false,
      procedureGroup: undefined,
      procedureGroupId: undefined,
      addEditModalOpen: false,
      tariffs: [],
      selectedTariffId: undefined,
      tarmedTariff: false,
      procedures: [],
      searchValues: initialProcedureGroupsSearch,
      availableProcedures: [],
      cancelTokenSource
    };

    getAllTariffs(cancelTokenSource)
        .then(response => {
          const tariffId = response[0].lnkTrfId != null ? response[0].lnkTrfId : response[0].id;
          this.setState({
            tariffs: response,
            selectedTariffId: tariffId,
            tarmedTariff: response[0].tarmedTariff
          })
        }).catch(this.handleError);
  }

  handleError() {
    const {getMessage} = this.props;
    this.setState({
      errorMessage: getMessage('error.general')
    })

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id: number | undefined) => {
    if (id) {
      findProcedureGroupById(id).then(response => {
        this.showModal(id, response);
      });
    }
  };

  showModal = (id?: number, procedureGroups?: UpsertProcedureGroupDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        procedureGroup: procedureGroups,
        procedureGroupId: id
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      procedureGroupId: undefined,
      procedureGroup: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        procedureGroupsLoaded: true
      })
    };

    searchProcedureGroup(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundProcedureGroups: response
          });
        })
        .catch(this.handleError)
        .finally(onFinally);
    this.hideModal();
  };

  deleteProcedureGroup = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteProcedureGroup(id, cancelTokenSource).then(response => {
      this.setState(({foundProcedureGroups}: State) => ({
        foundProcedureGroups: foundProcedureGroups.filter((procedureGroups: ProcedureGroupDto) => procedureGroups.id != response.id)
      }))
    }).catch(noop)
  };

  handleProcedureGroupsChange = debounce(({values}: ProcedureGroupsFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource, tariffs} = this.state;

    let valuesCopy = {...values, selection: 1};
    if (values.tariffId != null) {
      this.setState({procedureGroupsLoaded: false});

      const tariff = tariffs.find((tr) => {
        return tr.id == values.tariffId;
      });

      let tariffId = values.tariffId;
      if (tariff) {
        tariffId = tariff!.lnkTrfId != null ? tariff!.lnkTrfId : tariff!.id;
      }

      const onFinally = () => {
        this.setState({
          procedureGroupsLoaded: true
        })
      };

      searchProcedureGroup(valuesCopy, cancelTokenSource)
          .then(response =>
              this.setState({
                foundProcedureGroups: response,
                selectedTariffId: tariffId,
                tarmedTariff: tariff ? tariff.tarmedTariff : undefined,
                searchValues: values
              })
          )
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {procedureGroupsLoaded} = this.state;
    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchProcedureGroupsSegment()}
          </div>
          <StyledViewTableRow>
            {!procedureGroupsLoaded ? this.renderLoader() : this.renderProcedureGroupsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertProcedureGroupsModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchProcedureGroupsSegment = (): JSX.Element => {

    const {tariffs} = this.state;

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffId: tariffs.length > 0 ? tariffs[0].id : null
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;

    const {tariffs, foundProcedureGroups} = this.state;

    const numResults = foundProcedureGroups.length;

    const tariffCodeOptions = tariffs.map(tariffCode => {

      return {
        key: (tariffCode.id),
        text: tariffCode.description,
        value: tariffCode.id
      }
    });

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffId"
                  component={Select}
                  fluid
                  options={tariffCodeOptions}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("description")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  fluid
                  component={Input}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleProcedureGroupsChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              onConfirm={() => this.deleteProcedureGroup(cellData)}
              popupMessage={getMessage("procedureGroups.confirmDelete")}
          />
        </React.Fragment>
    );
  };

  procedureGroupsRowGetter = ({index}: any) => {
    const {foundProcedureGroups} = this.state;

    Object.assign(foundProcedureGroups[index], {index: index + 1});

    return foundProcedureGroups[index];
  };

  foundProceduresGroupsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundProcedureGroups} = this.state;

    const procedureGroupsId = foundProcedureGroups[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(procedureGroupsId)}
        >
          {columns}
        </div>
    );
  };

  renderProcedureGroupsSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundProcedureGroups} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundProcedureGroups.length}
            rowGetter={this.procedureGroupsRowGetter}
            rowRenderer={this.foundProceduresGroupsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 300,
                flexGrow: 1,
                flexShrink: 0,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("procedureGroup.loading")}</Loader>
        </Dimmer>
    );
  };

  renderDeleteButton = (): React.ReactNode => {
    const {getMessage} = this.props;
    return (
        <StyledActionButton icon>
          <Popup
              trigger={<Icon name={'delete'}/>}
              content={getMessage('msgDelete')}
              size="small"
              position="top center"
              style={PopupStyle}
              inverted
          />
        </StyledActionButton>
    );
  };

  renderUpsertProcedureGroupsModal = (): React.ReactNode => {

    const {addEditModalOpen, procedureGroup, procedureGroupId, selectedTariffId} = this.state;

    return (
        addEditModalOpen &&
        <UpsertProcedureGroupsForm
            procedureGroup={procedureGroup}
            procedureGroupId={procedureGroupId}
            tariffId={selectedTariffId}
            modalOpen={addEditModalOpen}
            onCancel={this.hideModal}
            onSave={this.onSaveUpsertForm}
        />
    );
  }

}

export default withMessages(ProcedureGroupsView);
